import { useEffect, useMemo, useRef } from "react";
import { motion } from "motion/react";
import { cx } from "class-variance-authority";
import * as styles from "./RollingCounter.module.scss";

type StaticColumn = {
  key: string;
  type: "static";
  char: string;
};

type DigitColumn = {
  key: string;
  type: "digit";
  char: string;
  previousChar?: string;
  sequence: string[];
  digitIndex: number;
};

type Column = StaticColumn | DigitColumn;

// Build up sequence of digits: 8 -> 2 becomes 8 -> 9 -> 0 -> 1 -> 2
const getDigitSequence = (
  fromChar: string | undefined,
  toChar: string,
  ascending: boolean,
) => {
  if (!fromChar || fromChar === toChar) return [toChar];

  const from = Number(fromChar);
  const to = Number(toChar);

  if (Number.isNaN(from) || Number.isNaN(to)) return [toChar];

  if (ascending) {
    // this gives us the number of steps rolling forward: 8->9->0->1->2 = 4 steps
    const steps = (to - from + 10) % 10;
    // build the sequence by stepping forward, wrapping 9->0 via %10
    return Array.from({ length: steps + 1 }, (_, i) => String((from + i) % 10));
  }

  // descending: same wrap trick but stepping backward
  // e.g. 2->8 rolling down: 2->1->0->9->8 = 4 steps, (2-8+10)%10 = 4
  const steps = (from - to + 10) % 10;
  // +10 before %10 keeps the result positive when subtracting past 0 (e.g. 0-1 = -1, (-1+10)%10 = 9)
  return Array.from({ length: steps + 1 }, (_, i) =>
    String((from - i + 10) % 10),
  );
};

// Split a string into an array of columns - static chars (symbol, separators, decimal point), and animated digit columns.
// each digit column knows its previous value so it can compute its scroll sequence.
const buildColumns = (
  formatted: string,
  previousChars: Record<string, string>,
  direction: number,
): Column[] => {
  const trimmed = formatted.trim();

  // find the first actual digit, it could be prepended with $ or something like AU$
  const firstDigitIdx = trimmed.search(/\d/);

  if (firstDigitIdx === -1) {
    return [{ key: "prefix-0", type: "static", char: trimmed }];
  }

  const lastDigitIdx = trimmed.search(/\d(?!.*\d)/);
  const prefix = trimmed.substring(0, firstDigitIdx);
  const suffix = trimmed.substring(lastDigitIdx + 1);
  const core = trimmed.substring(firstDigitIdx, lastDigitIdx + 1);

  const decimalIdx = core.indexOf(".");
  const integerStr = decimalIdx >= 0 ? core.substring(0, decimalIdx) : core;
  const decimalStr = decimalIdx >= 0 ? core.substring(decimalIdx + 1) : "";
  const intDigits = integerStr.replace(/\D/g, "");
  const decimalDigits = decimalStr.replace(/\D/g, "");
  const sepChar = integerStr.match(/[^\d]/)?.[0] ?? ",";
  const ascending = direction > 0;

  const prefixCols: Column[] = Array.from(prefix).map((char, i) => ({
    key: `prefix-${i}`,
    type: "static",
    char,
  }));

  const intCols: Column[] = Array.from(intDigits).flatMap((char, i, arr) => {
    const posFromRight = arr.length - 1 - i;
    const key = `int-${posFromRight}`;
    const previousChar = previousChars[key];
    const col: DigitColumn = {
      key,
      type: "digit",
      char,
      previousChar,
      sequence: getDigitSequence(previousChar, char, ascending),
      digitIndex: posFromRight + decimalDigits.length, // offset by decimal count so ones follows hundredths
    };
    const sep: Column[] =
      posFromRight > 0 && posFromRight % 3 === 0
        ? [{ key: `sep-${posFromRight}`, type: "static", char: sepChar }]
        : [];
    return [col, ...sep];
  });

  const decimalCols: Column[] =
    decimalIdx >= 0
      ? [
          { key: "decimal", type: "static", char: "." },
          ...Array.from(decimalDigits).map((char, i): DigitColumn => {
            const key = `dec-${i}`;
            const previousChar = previousChars[key];
            return {
              key,
              type: "digit",
              char,
              previousChar,
              sequence: getDigitSequence(previousChar, char, ascending),
              digitIndex: decimalDigits.length - 1 - i, // hundredths = 0, tenths = 1, right-to-left
            };
          }),
        ]
      : [];

  const suffixCols: Column[] = Array.from(suffix).map((char, i) => ({
    key: `suffix-${i}`,
    type: "static",
    char,
  }));

  return [...prefixCols, ...intCols, ...decimalCols, ...suffixCols];
};

const ROLL = {
  type: "spring",
  stiffness: 280,
  damping: 30,
  mass: 0.8,
} as const;

// Create a "strip" of numbers that we can animate through
const RollingDigit = ({
  column,
  shouldAnimate,
  direction,
}: {
  column: DigitColumn;
  shouldAnimate: boolean;
  direction: number;
}) => {
  const isDown = direction < 0;

  // as the strip animates, the first item in the array is always what's visible at rest.
  // for descending, reverse so the target digit is first and the strip slides down into it.
  const renderedSequence = isDown
    ? [...column.sequence].reverse()
    : column.sequence;

  // lower digitIndex = lower significance (hundredths, tenths) = animates first, creating a cascade effect
  const delay = shouldAnimate ? column.digitIndex * 0.01 : 0;

  // Each .digitInner is exactly 1em tall, so animate in em units.
  // Avoids DOM measurement which is unreliable in iOS WKWebView (returned 0 / stale height,
  // causing digits to render blank or stuck mid-track).
  const travelEm = `-${renderedSequence.length - 1}em`;
  const restY = isDown ? "0em" : travelEm;
  const startY = isDown ? travelEm : "0em";

  return (
    <span className={styles.digit}>
      <motion.span
        key={`${column.key}-${column.sequence.join("")}`}
        className={styles.digitTrack}
        initial={shouldAnimate ? { y: startY } : { y: restY }}
        animate={{ y: restY }}
        transition={shouldAnimate ? { ...ROLL, delay } : { duration: 0 }}
      >
        {renderedSequence.map((digit, index) => (
          <span
            key={`${column.key}-${digit}-${index}`}
            className={styles.digitInner}
          >
            {digit}
          </span>
        ))}
      </motion.span>
    </span>
  );
};

export type RollingCounterProps = {
  value: number;
  formatValue?: (value: number) => string;
  decimals?: number;
  className?: string;
};

export const RollingCounter = ({
  value,
  formatValue,
  decimals = 0,
  className,
}: RollingCounterProps) => {
  const didMountRef = useRef(false);
  const prevValueRef = useRef(value);
  const prevCharsRef = useRef<Record<string, string>>({});

  const formatted = formatValue ? formatValue(value) : value.toFixed(decimals);

  // Memoize on `formatted` so buildColumns only runs when the value actually changes.
  // Without this, every re-render (including after useEffect updates prevCharsRef) would
  // recompute columns with single-item sequences, changing the motion.span key mid-animation
  // and causing it to remount - killing the animation.
  const { columns, direction } = useMemo(() => {
    const dir =
      value > prevValueRef.current ? 1 : value < prevValueRef.current ? -1 : 0;
    return {
      direction: dir,
      columns: buildColumns(formatted, prevCharsRef.current, dir),
    };
  }, [formatted]);

  useEffect(() => {
    // update baseline every render
    didMountRef.current = true;
    prevValueRef.current = value;
    prevCharsRef.current = Object.fromEntries(
      columns.map((col) => [col.key, col.char]),
    );

    return () => {
      // reset baseline on unmount / Activity hide
      didMountRef.current = false;
      prevValueRef.current = value;
      prevCharsRef.current = {};
    };
  });

  return (
    <span className={cx(styles.rollingCounter, className)}>
      <span className={styles.container}>
        {columns.map((column) => (
          <span key={column.key} className={styles.column}>
            {column.type === "static" ? (
              <span className={styles.staticChar}>{column.char}</span>
            ) : (
              <RollingDigit
                column={column}
                shouldAnimate={
                  didMountRef.current &&
                  direction !== 0 &&
                  prevValueRef.current !== value &&
                  !!column.previousChar &&
                  column.previousChar !== column.char
                }
                direction={direction}
              />
            )}
          </span>
        ))}
      </span>
    </span>
  );
};
